import { HelpTopic } from './helpContent';

export const krHelpTopics: HelpTopic[] = [
  {
    id: 'kr-overview',
    label: 'Kr Overview',
    title: 'Relative Permeability in SCAL',
    intro:
      'Relative permeability measurements describe how easily each fluid flows through rock when multiple fluids are present. They are the primary input for fluid displacement and reservoir simulation workflows.',
    sections: [
      {
        id: 'what-is-kr',
        title: 'What relative permeability is',
        blocks: [
          {
            type: 'paragraph',
            text: 'Relative permeability (Kr) is the ratio of the effective permeability of a fluid to the absolute permeability of the rock at a given saturation. It quantifies how the presence of one fluid restricts the flow of another.',
          },
          {
            type: 'bullets',
            items: [
              'Kro — oil relative permeability: how freely oil flows at a given water saturation.',
              'Krw — water relative permeability: how freely water flows at a given water saturation.',
              'Both curves span from their saturation endpoints (Swi to 1 − Sor) and reach zero at the opposing endpoint.',
            ],
          },
        ],
      },
      {
        id: 'why-kr-matters',
        title: 'Why it is performed',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Reservoir simulation inputs: Kr curves are the primary drivers of fluid saturation in dynamic models.',
              'Displacement efficiency: Kr curves determine how efficiently water displaces oil during waterflooding.',
              'Wettability characterization: endpoint values and crossover saturation reveal rock surface preferences for water or oil.',
              'EOR design: mobility ratio calculations from Kr govern the stability of injection fronts.',
            ],
          },
        ],
      },
      {
        id: 'what-kr-provides',
        title: 'What this analysis provides',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Kro and Krw curves — measured (Lab) and modeled (Calc) relative permeability as a function of water saturation.',
              'Fw — fractional flow curve describing the fraction of total flow that is water.',
              'Saturation endpoints — Swi (irreducible water) and Sor (residual oil).',
              'Corey exponents — No and Nw controlling curve shape.',
              'Quality indicators — R² for each power-law fit, No+Nw sum, and crossover saturation.',
            ],
          },
          {
            type: 'note',
            title: 'Test method',
            text: 'This analysis is based on the Unsteady-State (USS) Water-Oil flooding test. The sample is flooded with brine at a controlled rate and the produced fluids are measured to derive Kro and Krw at successive saturations.',
          },
        ],
      },
    ],
  },
  {
    id: 'kr-saturations',
    label: 'Saturations',
    title: 'Key Saturation Concepts',
    intro:
      'Before interpreting relative permeability curves, it is essential to understand the saturation endpoints and the normalized saturation variables used in the Corey model.',
    sections: [
      {
        id: 'swi-sor',
        title: 'Saturation endpoints',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Swi — Irreducible Water Saturation',
                description: 'The minimum water saturation that cannot be displaced by oil under drainage conditions.',
                effect: 'Defines where Krw = 0 and Kro is at its maximum. Increasing Swi shifts both curves to the right and compresses the mobile saturation range.',
              },
              {
                name: 'Sor — Residual Oil Saturation',
                description: 'The minimum oil saturation remaining after waterflooding is complete.',
                effect: 'Defines where Kro = 0 and Krw is at its maximum. Lower Sor means more oil is recoverable before water dominates.',
              },
            ],
          },
        ],
      },
      {
        id: 'normalized-saturations',
        title: 'Normalized saturations (Son and Swn)',
        blocks: [
          {
            type: 'paragraph',
            text: 'The Corey model uses normalized saturations so that curve endpoints always fall between 0 and 1, regardless of Swi and Sor values. This makes parameters comparable across samples.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Son — Normalized Oil Saturation',
                description: 'Son = (1 − Sw − Sor) / (1 − Swi − Sor). Rescales oil saturation to 1 at Swi and 0 at Sor.',
                effect: 'Used as the independent variable in the Kro equation. Son = 1 when only oil is mobile; Son = 0 when residual oil is reached.',
              },
              {
                name: 'Swn — Normalized Water Saturation',
                description: 'Swn = (Sw − Swi) / (1 − Swi − Sor). Rescales water saturation to 0 at Swi and 1 at 1 − Sor.',
                effect: 'Used as the independent variable in the Krw equation. Swn = 0 when water is immobile; Swn = 1 at maximum water saturation.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'kr-corey',
    label: 'Corey Model',
    title: 'Corey Methodology',
    intro:
      'The Corey model represents relative permeability curves using power-law equations with normalized saturations. It is the standard empirical framework used in SCAL workflows and reservoir simulation.',
    sections: [
      {
        id: 'corey-overview',
        title: 'Overview',
        blocks: [
          {
            type: 'paragraph',
            text: 'The Corey model fits laboratory Kro and Krw measurements with simple power-law equations. Its parameters are physically interpretable, compact, and accepted directly by reservoir simulators.',
          },
          {
            type: 'bullets',
            items: [
              'Two equations — one for oil (Kro) and one for water (Krw) — each with its own exponent and endpoint.',
              'Normalized saturations (Son, Swn) ensure that the equations always produce values between 0 and 1.',
              'The model is calibrated by adjusting four main parameters: No, Nw, Kro\', and Krw\'.',
            ],
          },
        ],
      },
      {
        id: 'corey-equations',
        title: 'Corey equations',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Kro = Kro\' × Son^No',
                description: 'Kro\' is the oil endpoint (maximum Kro at Swi). No is the Corey exponent controlling oil curve shape.',
                effect: 'Son^No creates the concave shape of the Kro curve. Increasing No makes the curve drop more steeply near Sor.',
              },
              {
                name: 'Krw = Krw\' × Swn^Nw',
                description: 'Krw\' is the water endpoint (maximum Krw at Sor). Nw is the Corey exponent controlling water curve shape.',
                effect: 'Swn^Nw creates the concave shape of the Krw curve. Increasing Nw delays the rise of Krw toward higher saturations.',
              },
            ],
          },
        ],
      },
      {
        id: 'corey-inputs',
        title: 'Input variables',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Core Sample Length',
                description: 'Physical length of the core plug used in the laboratory experiment, expressed in centimeters. This is the actual plug dimension as cut from the whole core.',
                effect: 'Does not affect the Corey fitting, Kr curve generation, or any Fw calculation. It is used as the thickness weight (h) in the Stratigraphic Lorenz chart on the Zones page — φ×h and k×h integrals. If left blank, the Lorenz calculation assigns a unit weight of 1 to this sample.',
              },
              {
                name: 'Porosity',
                description: 'Fraction of the rock volume available for fluids.',
                effect: 'Provides petrophysical context for rock-typing and FZI comparison across samples.',
              },
              {
                name: 'Permeability (Kabs)',
                description: 'Absolute permeability of the core at 100% brine saturation.',
                effect: 'Reference value for normalizing Kr endpoints. Used with porosity for rock quality comparison.',
              },
              {
                name: 'Swi',
                description: 'Irreducible water saturation — lower bound of the mobile saturation window.',
                effect: 'Defines where Krw = 0 and the start of the mobile oil range.',
              },
              {
                name: 'Sor',
                description: 'Residual oil saturation — upper saturation bound where Kro = 0.',
                effect: 'Defines the end of the mobile oil range and the maximum achievable Krw.',
              },
              {
                name: 'Kro\'',
                description: 'Maximum relative permeability for oil, measured at Swi.',
                effect: 'Shifts the entire Kro curve up or down. Values well below 1.0 suggest wettability effects or incomplete drainage.',
              },
              {
                name: 'Krw\'',
                description: 'Maximum relative permeability for water, measured at Sor.',
                effect: 'Shifts the Krw curve. Typically 0.3–0.5 for water-wet rock; can exceed 0.8 in oil-wet or mixed-wet conditions.',
              },
              {
                name: 'No',
                description: 'Corey exponent for the oil phase.',
                effect: 'Controls the concavity of the Kro curve. Higher No = Kro stays high over a wider saturation range before dropping near Sor.',
              },
              {
                name: 'Nw',
                description: 'Corey exponent for the water phase.',
                effect: 'Controls the concavity of the Krw curve. Higher Nw = Krw rises slowly at low Sw and accelerates near Sor.',
              },
              {
                name: 'Oil Viscosity (μo)',
                description: 'Viscosity of the oil phase used in fractional flow calculations.',
                effect: 'Higher μo shifts the Fw curve, delaying water breakthrough and improving displacement efficiency.',
              },
              {
                name: 'Water Viscosity (μw)',
                description: 'Viscosity of the brine phase used in fractional flow calculations.',
                effect: 'Lower μw relative to μo leads to a less favorable mobility ratio.',
              },
              {
                name: 'Temperature (Tres)',
                description: 'Reservoir temperature, used as a context parameter.',
                effect: 'Affects fluid viscosities when comparing lab measurements with reservoir conditions.',
              },
            ],
          },
        ],
      },
      {
        id: 'corey-quality',
        title: 'No + Nw quality indicator',
        blocks: [
          {
            type: 'note',
            title: 'Expected range: 7.0 – 8.5',
            text: 'The sum of the two Corey exponents (No + Nw) is a standard quality check. Values within 7.0–8.5 indicate a well-behaved system. Values outside this range may signal data noise, outliers, or non-standard wetting behavior that requires manual review.',
          },
          {
            type: 'bullets',
            items: [
              'No + Nw < 7.0 — exponents may be underestimated; check for noisy data at saturation endpoints.',
              'No + Nw > 8.5 — exponents may be overestimated; check for mixed-wet behavior or unreliable data near Sor.',
              'When the sum is outside range, use the local exponent plots (No\', Nw\') to diagnose the cause before adjusting.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'kr-parameters',
    label: 'Parameters',
    title: 'Parameters and Curve Shape',
    intro:
      'Each Corey parameter controls a distinct aspect of the Kr curves. Understanding their individual effects helps calibrate the model to match laboratory data.',
    sections: [
      {
        id: 'exponent-effects',
        title: 'Effect of Corey exponents',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'No — Oil Exponent',
                description: 'Controls the shape of the Kro curve.',
                effect: 'Higher No: Kro remains near its maximum over a wider saturation range, then drops steeply near Sor. Lower No: Kro declines more gradually from Swi onward. Strongly water-wet rocks tend to have higher No.',
              },
              {
                name: 'Nw — Water Exponent',
                description: 'Controls the shape of the Krw curve.',
                effect: 'Higher Nw: Krw rises slowly at low Sw and accelerates near Sor. Lower Nw: Krw rises steeply as soon as water begins to flow. Oil-wet or mixed-wet rocks tend to have lower Nw.',
              },
              {
                name: 'Kro\' — Oil Endpoint',
                description: 'Maximum value of Kro at Swi.',
                effect: 'Shifts the entire Kro curve up or down without changing its shape. Values significantly below 1.0 indicate that oil is not flowing at full capacity even at Swi, which is a wettability signal.',
              },
              {
                name: 'Krw\' — Water Endpoint',
                description: 'Maximum value of Krw at Sor.',
                effect: 'Shifts the entire Krw curve up or down. In water-wet rocks Krw\' is typically 0.3–0.5; in oil-wet or mixed-wet rocks it can exceed 0.8.',
              },
            ],
          },
        ],
      },
      {
        id: 'crossover-point',
        title: 'Crossover point (Sw @ Kro = Krw)',
        blocks: [
          {
            type: 'paragraph',
            text: 'The crossover point is the water saturation at which Kro and Krw are equal. It is one of the most important wettability indicators derived from Kr analysis.',
          },
          {
            type: 'bullets',
            items: [
              'Crossover Sw > 0.5 — water-wet: water must saturate more than half the pore space before it flows as freely as oil.',
              'Crossover Sw < 0.5 — oil-wet or mixed-wet: water flows as freely as oil at a relatively low water saturation.',
              'The crossover saturation is displayed in the Refinement panel and is used as a quality check against the Corey fit.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'kr-wettability',
    label: 'Wettability',
    title: 'Wettability Assessment',
    intro:
      'Wettability describes the preference of the rock surface for one fluid over another. It controls the shape of both the Kro and Krw curves and is directly observable from saturation endpoints and crossover behavior.',
    sections: [
      {
        id: 'wettability-types',
        title: 'Wettability types',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Water-wet',
                description: 'Rock surface preferentially contacts water. Water occupies small pores and coats grain surfaces.',
                effect: 'Kro\' close to 1.0, Krw\' low (0.3–0.5), crossover Sw > 0.5. Kro drops rapidly after water breakthrough.',
              },
              {
                name: 'Oil-wet',
                description: 'Rock surface preferentially contacts oil. Oil occupies small pores; water flows through larger pores.',
                effect: 'Kro\' well below 1.0, Krw\' high (can approach 1.0), crossover Sw < 0.5. Krw rises steeply at low Sw.',
              },
              {
                name: 'Mixed-wet',
                description: 'Some pores are water-wet, others are oil-wet. Intermediate behavior with non-uniform pore-scale wetting.',
                effect: 'Intermediate endpoints and crossover Sw close to 0.5. Local exponents (No\', Nw\') vary with saturation — a single Corey exponent is often a poor representation.',
              },
            ],
          },
        ],
      },
      {
        id: 'mixed-wet-identification',
        title: 'Identifying mixed-wet behavior in the app',
        blocks: [
          {
            type: 'bullets',
            items: [
              'The No\' and Nw\' vs. Sw plots show non-constant values (not a horizontal line) — the local exponent varies with saturation.',
              'The Kro/Krw ratio at the crossover point differs between the lab measurement and the Corey fit, producing a non-zero error.',
              'The comment "Not Straight Line" in the refinement output signals that the linear regression on the log-log plot is a poor fit.',
              'When mixed-wet behavior is detected, review the Corey fit critically and consider whether a single exponent adequately represents the data.',
            ],
          },
          {
            type: 'note',
            title: 'Sample P2-H4 context',
            text: 'The sample in the H4 worksheet is classified as Mix-Wet. The local exponent analysis (No\', Nw\') shows non-constant behavior, and the log-log fit is flagged as "Not Straight Line." These are the signatures to look for when assessing wettability from your own samples.',
          },
        ],
      },
    ],
  },
  {
    id: 'kr-local-exp',
    label: 'Local Exponents',
    title: 'Local Exponent Analysis (No\' and Nw\')',
    intro:
      'Local exponents reveal how the effective Corey exponent changes across the saturation range. They are a diagnostic tool for validating whether a single Corey exponent is appropriate for the sample.',
    sections: [
      {
        id: 'local-exp-definition',
        title: 'What local exponents are',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'No\' — Local Oil Exponent',
                description: 'No\' = log(Kro) / log(Son). Computed at each measured data point.',
                effect: 'If No\' is approximately constant across all Sw values, a single Corey No fits well. If No\' varies, the Kro behavior is non-uniform — typical of mixed-wet rock.',
              },
              {
                name: 'Nw\' — Local Water Exponent',
                description: 'Nw\' = [log(Krw\') − log(Krw)] / [log(1) − log(Swn)]. Computed at each measured data point.',
                effect: 'If Nw\' is approximately constant, a single Nw fits well. A trending or scattered Nw\' signals heterogeneous wetting or data noise at specific saturation intervals.',
              },
            ],
          },
        ],
      },
      {
        id: 'local-exp-interpretation',
        title: 'How to interpret the charts',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Flat No\' line → single Corey exponent is appropriate for oil; accept the No from the log-log fit.',
              'Flat Nw\' line → single Corey exponent is appropriate for water; accept the Nw from the log-log fit.',
              'Trending No\' or Nw\' → mixed-wet or heterogeneous rock; use the average value as a starting point and adjust manually.',
              'Extreme outliers in No\' or Nw\' (near Sor or Swi) are common due to measurement noise at saturation limits — consider removing those data points from the regression.',
            ],
          },
          {
            type: 'note',
            title: 'Use with Find Best Values',
            text: 'Run Find Best Values first, then check the local exponent plots. If No\' or Nw\' are not flat, manually adjust the corresponding exponent and re-evaluate the No+Nw sum.',
          },
        ],
      },
    ],
  },
  {
    id: 'kr-fw',
    label: 'Fractional Flow',
    title: 'Fractional Flow Analysis',
    intro:
      'The fractional flow curve (Fw) describes what fraction of the total flow is water at each saturation. It connects Kr results to displacement efficiency and flood performance.',
    sections: [
      {
        id: 'fw-definition',
        title: 'What fractional flow is',
        blocks: [
          {
            type: 'paragraph',
            text: 'Fractional flow is computed from both the lab Kr measurements and the Corey model fit. It ranges from 0 (only oil flowing, at Swi) to 1 (only water flowing, at Sor).',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Fw formula',
                description: 'Fw = 1 / (1 + (Kro / Krw) × (μw / μo))',
                effect: 'Fw depends on the ratio Kro/Krw and the viscosity ratio. Lower oil viscosity or higher Krw raises Fw at a given saturation, meaning water breaks through sooner.',
              },
            ],
          },
        ],
      },
      {
        id: 'fw-usage',
        title: 'How to use the Fw curve',
        blocks: [
          {
            type: 'bullets',
            items: [
              'At Swi: Fw = 0 — only oil flows; no water production.',
              'At Sor: Fw = 1 — only water flows; no remaining mobile oil.',
              'The inflection point of the Fw curve corresponds to the optimal water injection front (Buckley-Leverett tangent construction).',
              'Mismatch between Lab Fw and Calc Fw at mid-saturations usually means No or Nw needs adjustment.',
            ],
          },
        ],
      },
      {
        id: 'displacement-efficiency',
        title: 'Displacement efficiency and mobility ratio',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Ed @ BT — Displacement Efficiency at Breakthrough',
                description: 'The fraction of movable oil recovered when water first reaches the producing end of the core.',
                effect: 'Higher Ed means more oil is produced before water cuts the flow. Controlled by the shape of the Fw curve and the viscosity ratio.',
              },
              {
                name: 'Mobility Ratio (M)',
                description: 'M = (Krw / μw) / (Kro / μo), evaluated at the saturation where water breaks through.',
                effect: 'M < 1 — favorable: the water front is stable and displacement is efficient. M > 1 — unfavorable: viscous fingering risk, lower sweep efficiency. Used to assess EOR and injection strategy.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'kr-fitting',
    label: 'Find Best Values',
    title: 'Curve Fitting — Find Best Values',
    intro:
      'Find Best Values runs a power-law regression on the normalized Kr data to estimate the Corey parameters that best match the laboratory measurements.',
    sections: [
      {
        id: 'fitting-what-it-does',
        title: 'What the optimizer does',
        blocks: [
          {
            type: 'paragraph',
            text: 'The system performs least-squares regression on log-log plots of Son vs. Kro and Swn vs. Krw. This yields the power-law coefficients (A, B) that minimize deviation from the measured data.',
          },
          {
            type: 'bullets',
            items: [
              'Fits a power equation (y = a × x^b) to the log-log transformed data for each phase.',
              'The slope of the fitted line corresponds to the Corey exponent (No or Nw).',
              'The intercept determines the endpoint (Kro\' or Krw\').',
              'Reports R² for each fit — values above 0.95 indicate a reliable regression.',
            ],
          },
        ],
      },
      {
        id: 'fitting-expected-outputs',
        title: 'What to expect after running',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Updated No and Nw values from the regression.',
              'Updated Kro\' and Krw\' endpoint estimates.',
              'Recalculated Kro Calc, Krw Calc, and Fw Calc curves.',
              'A new No+Nw sum — verify it falls within the expected 7.0–8.5 range.',
              'Crossover Sw updated to reflect the new model parameters.',
            ],
          },
        ],
      },
      {
        id: 'fitting-manual-adjustment',
        title: 'When to adjust manually',
        blocks: [
          {
            type: 'bullets',
            items: [
              'No+Nw sum is outside the 7.0–8.5 range after fitting.',
              'The Fw Calc curve does not match the shape of the Lab Fw curve.',
              'The wettability interpretation (crossover Sw) conflicts with expected rock behavior.',
              'Outlier data points are pulling the regression; remove unreliable rows and refit.',
            ],
          },
          {
            type: 'note',
            title: 'Recommended workflow',
            text: 'Run Find Best Values as a starting point, then check the No+Nw sum, local exponent plots (No\', Nw\'), and Fw mismatch. Make targeted manual adjustments to one parameter at a time, and check all three quality indicators after each change.',
          },
        ],
      },
    ],
  },
  {
    id: 'kr-charts',
    label: 'Charts',
    title: 'Charts and Interpretation',
    intro:
      'The Kr Analysis tab provides multiple charts for calibrating the Corey model and assessing data quality. Each chart targets a specific aspect of the analysis.',
    sections: [
      {
        id: 'chart-kro-krw',
        title: 'Kro and Krw vs. Sw',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Scatter points — Lab measurements of Kro and Krw at each measured water saturation.',
              'Lines — Corey model curves (Kro Calc and Krw Calc) computed from the current parameters.',
              'Look for: how closely the model lines follow the lab points across the full saturation range.',
              'Crossover point location: Sw < 0.5 signals mixed-wet or oil-wet; Sw > 0.5 signals water-wet.',
              'If curves diverge at low Sw, adjust No or Kro\'. If they diverge at high Sw, adjust Nw or Krw\'.',
            ],
          },
        ],
      },
      {
        id: 'chart-fw',
        title: 'Fractional Flow (Fw) vs. Sw',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Lab Fw — computed from measured Kro and Krw using the Fw formula.',
              'Fw Calc — computed from the Corey model curves.',
              'Match between Lab Fw and Calc Fw validates that the Corey parameters reproduce not just individual Kr values but the combined displacement behavior.',
              'Fw mismatch at mid-saturations usually means one of the exponents (No or Nw) needs adjustment.',
              'Steep slope near Swi → good early displacement efficiency; flat slope → slow start, poor sweep.',
            ],
          },
        ],
      },
      {
        id: 'chart-loglog',
        title: 'Log-log plots (Son vs. Kro, Swn vs. Krw)',
        blocks: [
          {
            type: 'paragraph',
            text: 'These are the fitting plots used by the regression. Linear behavior on a log-log scale confirms that the Corey power-law model is appropriate for the sample.',
          },
          {
            type: 'bullets',
            items: [
              'Son vs. Kro: a straight line on the log-log plot confirms a single No exponent fits the oil data.',
              'Swn vs. Krw: a straight line confirms a single Nw exponent fits the water data.',
              'R² > 0.95 indicates a reliable power-law fit for that phase.',
              'Non-linear (curved) scatter on either plot is a sign of mixed-wet rock — single-exponent Corey may be insufficient.',
              'If individual points deviate significantly, check whether they are reliable measurements and consider excluding outliers.',
            ],
          },
        ],
      },
      {
        id: 'chart-local-exp',
        title: 'No\' and Nw\' vs. Sw',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Shows the instantaneous (local) Corey exponent at each measured saturation.',
              'A horizontal line at a constant value → single Corey exponent is an appropriate representation.',
              'A trending or scattered line → non-uniform wetting behavior; consider using the average No\' or Nw\' as the starting exponent.',
              'Extreme values near Swi or Sor are common due to measurement noise at saturation limits.',
            ],
          },
        ],
      },
      {
        id: 'chart-kro-krw-ratio',
        title: 'Kro/Krw ratio',
        blocks: [
          {
            type: 'paragraph',
            text: 'The Kro/Krw ratio at the crossover saturation is computed from both the lab data and the Corey model. The error between them is a direct quality check on how well the model captures the crossover behavior.',
          },
          {
            type: 'bullets',
            items: [
              'Error close to zero → the model reproduces the lab crossover faithfully.',
              'Large error → the crossover saturation is not being captured correctly; adjust No, Nw, or the endpoints.',
              'This ratio can also be used to compare wettability state across multiple samples from the same well or reservoir.',
            ],
          },
        ],
      },
    ],
  },
];
